import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './routes';
import { ToastProvider } from './components';
import { DevThemeSwitcher, ThemeProvider } from './theme';
import { useSurface } from './hooks/useSurface';
import { initTelegram } from './telegram/sdk';
import { ApiError } from './api/client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      // Telegram webviews (and some embedded browsers) misreport
      // `navigator.onLine`, which leaves React Query stuck in `fetchStatus:
      // 'paused'` and the UI stuck on skeletons. Always attempt the request and
      // let the failure surface as an error state.
      networkMode: 'always',
      retry: (failureCount, error) => {
        // An HTTP status is an answer, not a blip — retrying a 403 or a 500
        // just delays the error state. Only transport failures get a second
        // chance.
        if (error instanceof ApiError) return false;
        return failureCount < 2;
      },
    },
    mutations: { networkMode: 'always' },
  },
});

/** Boots the SDK (Telegram only) and keeps the surface CSS vars in sync. */
function SurfaceBoot() {
  useSurface();
  useEffect(() => {
    void initTelegram();
  }, []);
  return null;
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            <SurfaceBoot />
            <AppRoutes />
          </BrowserRouter>
          <DevThemeSwitcher />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
